/**
 * Loading skeleton components with refined design
 * Uses consistent styling with the rest of the application
 */
import { useTranslation } from 'react-i18next';

interface SkeletonProps {
  className?: string;
}

/**
 * Base skeleton component with shimmer animation
 */
export function Skeleton({ className = '' }: SkeletonProps) {
  return <div className={`skeleton ${className}`} />;
}

/**
 * Product card skeleton for product listings
 */
export function ProductCardSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-soft border border-stone-100 overflow-hidden">
      {/* Image placeholder */}
      <div className="aspect-square skeleton" />

      {/* Content */}
      <div className="p-5">
        {/* Title */}
        <div className="skeleton h-5 w-3/4 mb-2" />

        {/* Subtitle */}
        <div className="skeleton h-4 w-1/2 mb-4" />

        {/* Price and CTA */}
        <div className="flex items-end justify-between">
          <div className="space-y-1">
            <div className="skeleton h-3 w-8" />
            <div className="skeleton h-7 w-20" />
          </div>
          <div className="skeleton h-10 w-10 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

/**
 * Product grid skeleton
 */
export function ProductGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-6 sm:gap-x-6 sm:gap-y-10">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{ animationDelay: `${i * 0.05}s` }}
          className="animate-fade-in"
        >
          <ProductCardSkeleton />
        </div>
      ))}
    </div>
  );
}

/**
 * Product detail page skeleton
 */
export function ProductDetailSkeleton() {
  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumbs */}
          <div className="skeleton h-4 w-48 mb-6 rounded-lg" />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image gallery */}
            <div className="space-y-4">
              <div className="aspect-square skeleton rounded-2xl" />
              <div className="flex gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-20 h-20 skeleton rounded-lg flex-shrink-0"
                  />
                ))}
              </div>
            </div>

            {/* Product info */}
            <div className="space-y-6">
              {/* Category badge */}
              <div className="skeleton h-8 w-32 rounded-full" />

              {/* Title */}
              <div className="skeleton h-9 w-3/4 rounded-lg" />

              {/* Description */}
              <div className="space-y-2">
                <div className="skeleton h-4 w-full rounded" />
                <div className="skeleton h-4 w-5/6 rounded" />
              </div>

              {/* Price card */}
              <div className="bg-white rounded-xl p-4 shadow-soft border border-stone-100">
                <div className="skeleton h-9 w-32 rounded-lg" />
              </div>

              {/* Size selector */}
              <div className="bg-white rounded-xl p-4 shadow-soft border border-stone-100">
                <div className="skeleton h-5 w-20 mb-3 rounded" />
                <div className="flex gap-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton h-10 w-16 rounded-lg" />
                  ))}
                </div>
              </div>

              {/* Upload area */}
              <div className="bg-white rounded-xl p-4 shadow-soft border border-stone-100">
                <div className="skeleton h-5 w-40 mb-3 rounded" />
                <div className="skeleton h-32 w-full rounded-xl" />
              </div>

              {/* Button */}
              <div className="skeleton h-14 w-full rounded-xl" />

              {/* Features */}
              <div className="bg-white rounded-xl p-4 shadow-soft border border-stone-100 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="skeleton w-8 h-8 rounded-full flex-shrink-0" />
                    <div className="space-y-1 flex-1">
                      <div className="skeleton h-4 w-32 rounded" />
                      <div className="skeleton h-3 w-48 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Cart item skeleton
 */
export function CartItemSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-soft border border-stone-100 p-5">
      <div className="flex gap-4">
        {/* Image */}
        <div className="w-24 h-24 sm:w-32 sm:h-32 skeleton rounded-xl flex-shrink-0" />

        {/* Details */}
        <div className="flex-1 space-y-2">
          <div className="flex justify-between">
            <div className="skeleton h-5 w-3/4 rounded" />
            <div className="skeleton h-8 w-8 rounded-lg" />
          </div>
          <div className="skeleton h-4 w-1/2 rounded" />
          <div className="flex gap-2 mt-3">
            <div className="skeleton h-6 w-16 rounded-lg" />
            <div className="skeleton h-6 w-20 rounded-lg" />
          </div>
          <div className="flex justify-between items-center mt-4">
            <div className="skeleton h-10 w-28 rounded-xl" />
            <div className="skeleton h-7 w-24 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Table row skeleton
 */
export function TableRowSkeleton({ columns = 5 }: { columns?: number }) {
  return (
    <tr className="border-b border-stone-100">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="skeleton h-4 rounded" />
        </td>
      ))}
    </tr>
  );
}

/**
 * Full page loading skeleton
 */
export function PageLoadingSkeleton({ message }: { message?: string }) {
  const { t } = useTranslation();
  const resolvedMessage = message ?? t('common.loading');
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center p-4">
      <div className="relative">
        {/* Animated logo placeholder */}
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center shadow-glow-primary animate-pulse-soft">
          <span className="text-white font-heading font-bold text-2xl">G</span>
        </div>
        {/* Spinner ring */}
        <div
          className="absolute inset-0 -m-1.5 border-3 border-primary-200 border-t-primary-500 rounded-2xl animate-spin"
          style={{ animationDuration: '1s' }}
        />
      </div>
      <p className="mt-5 text-stone-600 font-medium">{resolvedMessage}</p>
    </div>
  );
}

/**
 * Inline loading spinner
 */
export function LoadingSpinner({
  size = 'md',
  message,
}: {
  size?: 'sm' | 'md' | 'lg';
  message?: string;
}) {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-8 h-8 border-3',
    lg: 'w-12 h-12 border-4',
  };

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      <div
        className={`${sizeClasses[size]} border-stone-200 border-t-primary-500 rounded-full animate-spin`}
      />
      {message && <p className="text-sm text-stone-500">{message}</p>}
    </div>
  );
}

/**
 * Button loading state
 */
export function ButtonLoading({ text }: { text?: string }) {
  const { t } = useTranslation();
  const resolvedText = text ?? t('common.pleaseWait');
  return (
    <span className="flex items-center justify-center gap-2">
      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      {resolvedText}
    </span>
  );
}

/**
 * Content placeholder with shimmer effect
 */
export function ContentSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="skeleton h-4 rounded"
          style={{ width: `${100 - i * 10}%` }}
        />
      ))}
    </div>
  );
}

/**
 * Designer loading skeleton
 */
export function DesignerSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-stone-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="skeleton h-8 w-48 mb-2 rounded-lg" />
          <div className="skeleton h-4 w-72 rounded" />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Left sidebar */}
          <div className="xl:col-span-3 space-y-6">
            <div className="bg-white rounded-2xl shadow-soft border border-stone-100 p-6">
              <div className="skeleton h-6 w-32 mb-4 rounded-lg" />
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton h-12 rounded-xl" />
                ))}
              </div>
            </div>
          </div>

          {/* Canvas */}
          <div className="xl:col-span-6">
            <div className="bg-white rounded-2xl shadow-soft border border-stone-100 p-4">
              <div className="aspect-[6/7] skeleton rounded-xl flex items-center justify-center">
                <div className="text-center text-stone-400">
                  <svg
                    className="w-16 h-16 mx-auto mb-2 opacity-50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="text-sm">{t('common.designerLoading')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right sidebar */}
          <div className="xl:col-span-3 space-y-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-soft border border-stone-100 p-6"
              >
                <div className="skeleton h-5 w-24 mb-4 rounded-lg" />
                <div className="space-y-2">
                  <div className="skeleton h-4 rounded" />
                  <div className="skeleton h-4 w-3/4 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Order tracking skeleton
 */
export function OrderTrackingSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-soft border border-stone-100 p-6">
      {/* Order number */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="skeleton h-4 w-24 mb-2 rounded" />
          <div className="skeleton h-6 w-40 rounded-lg" />
        </div>
        <div className="skeleton h-8 w-24 rounded-full" />
      </div>

      {/* Timeline */}
      <div className="space-y-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="skeleton w-10 h-10 rounded-full flex-shrink-0" />
            <div className="flex-1">
              <div className="skeleton h-4 w-32 mb-1.5 rounded" />
              <div className="skeleton h-3 w-24 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Card skeleton for general use
 */
export function CardSkeleton({ height = 'auto' }: { height?: string }) {
  return (
    <div
      className="bg-white rounded-2xl shadow-soft border border-stone-100 p-6"
      style={{ height }}
    >
      <div className="skeleton h-6 w-1/3 mb-4 rounded-lg" />
      <div className="space-y-3">
        <div className="skeleton h-4 w-full rounded" />
        <div className="skeleton h-4 w-5/6 rounded" />
        <div className="skeleton h-4 w-4/6 rounded" />
      </div>
    </div>
  );
}

/**
 * Stats card skeleton
 */
export function StatsSkeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-soft border border-stone-100 p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <div className="skeleton h-4 w-20 rounded" />
          <div className="skeleton h-8 w-16 rounded-lg" />
        </div>
        <div className="skeleton w-12 h-12 rounded-xl" />
      </div>
    </div>
  );
}
